import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs/operators';
import {
  AdminUsersRoutes,
  RoutesEnum,
  UserUsersAndPaxRoutes,
} from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { UserOptionsApiService } from 'src/app/core/services/user-options-api.service';
import { MenuListLogicService } from 'src/app/core/services/menu-list-logic.service';
import { PatchProfilePicture } from 'src/app/tenant-selector/state/tenant-user-access.actions';
import { UserPreferencesLogicService } from 'src/app/profile/services/user-preferences-logic.service';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import {
  IUserProductOwnerView,
  IUserModel,
  IUserTable,
  IUserSearchResult,
  IUserListResult,
} from '../models/user.model';
import { OnSuccess } from '../../decorators/on-success.decorator';
import { IUserOptions } from '../models/user-options.model';
import { fullName } from '../../functions/full-name';

import { UserApiService } from '../services/user-api.service';
import {
  CreateUser,
  DeactivateUser,
  PauseUserEmployments,
  DeleteUserEmployments,
  GetMyUser,
  GetUserById,
  LoadAllUsersAllTenants,
  LoadUserOptions,
  LoadUsers,
  ReactivateUser,
  ResendConfirmationEmail,
  RestoreUser,
  ResumeUserEmployment,
  SendResetPasswordEmail,
  UpdateMyUser,
  UpdateUser,
  RestoreUserEmployment,
  ActivateUserAsProductOwner,
  DeactivateUserAsProductOwner,
  SearchUsers,
  SearchUsersInTenant,
  ClearCurrentUser,
  GetMySignature,
  LoadPlannedActivitySignOffPeriodsDocumentationCompleted,
  LoadModuleAdminNames,
} from './user.actions';
import { redirectAndBypassUnsavedDataCheck } from '../../functions/redirect-and-bypass-unsaved-data-check';
import { IAttachment } from '../../attachments/models/attachments.model';

export interface UserStateModel {
  currentUser?: IUserModel;
  users: IUserTable[];
  usersTotalPages: number;
  allUsersAllTenants: IUserProductOwnerView[];
  allUsersAllTenantsTotalPages: number;
  userOptions?: IUserOptions;
  userOptionsLoaded: boolean;
  showDeletedUsers: boolean;
  showPausedUsers: boolean;
  showDeactivatedUsers: boolean;
  searchUsersResults: IUserSearchResult[];
  moduleAdminNames: string[];
  mySignature?: IAttachment | undefined;
  plannedActivitySignOffPeriodsDocumentationCompleted?: boolean;
}

const USER_STATE_TOKEN = new StateToken<UserStateModel>('user');

@State<UserStateModel>({
  name: USER_STATE_TOKEN,
  defaults: {
    currentUser: undefined,
    users: [],
    allUsersAllTenants: [],
    allUsersAllTenantsTotalPages: 0,
    usersTotalPages: 0,
    userOptions: undefined,
    userOptionsLoaded: false,
    showDeletedUsers: false,
    showPausedUsers: false,
    showDeactivatedUsers: false,
    searchUsersResults: [],
    moduleAdminNames: [],
    mySignature: undefined,
    plannedActivitySignOffPeriodsDocumentationCompleted: undefined,
  },
})
@Injectable()
export class UserState {
  constructor(
    private service: UserApiService,
    private optionsService: UserOptionsApiService,
    private router: Router,
    private userPreferencesLogicService: UserPreferencesLogicService,
    private menuListLogicService: MenuListLogicService
  ) {}

  @Action(LoadUsers, { cancelUncompleted: true })
  loadUsers({ getState, setState }: StateContext<UserStateModel>, { filter }: LoadUsers) {
    return this.service.getAllUsers(filter).pipe(
      tap((response: ApiResponse<IUserTable[]>) => {
        const state = getState();
        let newState = {
          ...state,
          users: response.data,
          usersTotalPages: response.totalPages,
        };
        if (filter?.additional !== null && filter?.additional !== undefined) {
          newState = {
            ...newState,
            showDeletedUsers: filter.additional.includeDeleted,
            showPausedUsers: filter.additional.includePaused,
            showDeactivatedUsers: filter.additional.includeDeactivated,
          };
        }
        setState(newState);
      })
    );
  }

  @Action(LoadAllUsersAllTenants, { cancelUncompleted: true })
  loadAllUsersAllTenants(
    { setState }: StateContext<UserStateModel>,
    { filter }: LoadAllUsersAllTenants
  ) {
    return this.service.getAllUsersAllTenants(filter).pipe(
      tap((response: ApiResponse<IUserProductOwnerView[]>) => {
        setState(
          patch({
            allUsersAllTenantsTotalPages: response.totalPages,
            allUsersAllTenants: response.data,
            showDeletedUsers: filter?.additional?.includeDeleted,
            showPausedUsers: filter?.additional?.includePaused,
            showDeactivatedUsers: filter?.additional?.includeDeactivated,
          })
        );
      })
    );
  }

  @Action(LoadUserOptions, { cancelUncompleted: true })
  loadUserOptions({ getState, setState }: StateContext<UserStateModel>) {
    return this.optionsService.getOptions().pipe(
      tap((userOptions: IUserOptions) => {
        const state = getState();
        setState({
          ...state,
          userOptionsLoaded: true,
          userOptions,
        });
      })
    );
  }

  @Action(CreateUser, { cancelUncompleted: true })
  @OnSuccess({ message: 'userCreated' })
  createUser(ctx: StateContext<UserStateModel>, { user }: CreateUser) {
    return this.service.createUser(user).pipe(
      tap(() => {
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Users
          : UserUsersAndPaxRoutes.Users;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(UpdateUser, { cancelUncompleted: true })
  @OnSuccess({ message: 'userUpdated' })
  updateUser(ctx: StateContext<UserStateModel>, { user }: UpdateUser) {
    return this.service.updateUser(user).pipe(
      tap(() => {
        if (this.userPreferencesLogicService.getUserId() === user.id) {
          // eslint-disable-next-line no-use-before-define
          dispatchProfilePicture(ctx, user);
        }
        redirectAndBypassUnsavedDataCheck(
          this.menuListLogicService.isCurrentUrlInAdmin()
            ? AdminUsersRoutes.Users
            : UserUsersAndPaxRoutes.Users
        );
      })
    );
  }

  @Action(UpdateMyUser, { cancelUncompleted: true })
  @OnSuccess({ message: 'userUpdated' })
  updateMyUser(ctx: StateContext<UserStateModel>, { user }: UpdateMyUser) {
    return this.service.updateMyUser(user).pipe(
      tap(() => {
        // eslint-disable-next-line no-use-before-define
        dispatchProfilePicture(ctx, user);
        redirectAndBypassUnsavedDataCheck(`${RoutesEnum.Profile}/view/(details:edit-user)`);
      })
    );
  }

  @Action(GetUserById, { cancelUncompleted: true })
  getUserById({ getState, setState }: StateContext<UserStateModel>, { id }: GetUserById) {
    return this.service.getUserById(id).pipe(
      tap((user: IUserModel) => {
        const state = getState();
        setState({
          ...state,
          currentUser: user,
        });
      })
    );
  }

  @Action(GetMyUser, { cancelUncompleted: true })
  getMyUser({ getState, setState }: StateContext<UserStateModel>) {
    return this.service.getMyUser().pipe(
      tap((user: IUserModel) => {
        const state = getState();
        setState({
          ...state,
          currentUser: user,
        });
      })
    );
  }

  @Action(PauseUserEmployments, { cancelUncompleted: true })
  @OnSuccess({ message: 'userEmploymentPaused' })
  pauseUserEmployments(ctx: StateContext<UserStateModel>, { id }: PauseUserEmployments) {
    return this.service.pauseUserEmployments(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            users: removeItem<IUserTable>((user: IUserTable | undefined) => user?.id === id),
          })
        );
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Users
          : UserUsersAndPaxRoutes.Users;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(DeleteUserEmployments, { cancelUncompleted: true })
  @OnSuccess({ message: 'userEmploymentDeleted', dispatch: ResetCurrentFilter })
  deleteUserEmployments(ctx: StateContext<UserStateModel>, { id }: DeleteUserEmployments) {
    return this.service.deleteUserEmployments(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            users: removeItem<IUserTable>((user: IUserTable | undefined) => user?.id === id),
          })
        );
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Users
          : UserUsersAndPaxRoutes.Users;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(RestoreUser, { cancelUncompleted: true })
  @OnSuccess({ message: 'userRestored' })
  restoreUser(ctx: StateContext<UserStateModel>, { id }: RestoreUser) {
    return this.service.restoreUser(id).pipe(
      tap(() => {
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Users
          : UserUsersAndPaxRoutes.Users;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(ResumeUserEmployment, { cancelUncompleted: true })
  @OnSuccess({ message: 'userEmploymentResumed' })
  resumeUserEmployment(ctx: StateContext<UserStateModel>, { id }: ResumeUserEmployment) {
    return this.service.resumeUserEmployment(id).pipe(
      tap(() => {
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Users
          : UserUsersAndPaxRoutes.Users;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(RestoreUserEmployment, { cancelUncompleted: true })
  @OnSuccess({ message: 'userEmploymentRestored' })
  restoreUserEmployment(ctx: StateContext<UserStateModel>, { id }: RestoreUserEmployment) {
    return this.service.restoreUserEmployment(id).pipe(
      tap(() => {
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Users
          : UserUsersAndPaxRoutes.Users;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(DeactivateUser, { cancelUncompleted: true })
  @OnSuccess({ message: 'userDeactivated' })
  deactivateUser(ctx: StateContext<UserStateModel>, { id }: DeactivateUser) {
    return this.service.deactivateUser(id).pipe(
      tap(() => {
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Users
          : UserUsersAndPaxRoutes.Users;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(ReactivateUser, { cancelUncompleted: true })
  @OnSuccess({ message: 'userReactivated' })
  reactivateUser(ctx: StateContext<UserStateModel>, { id }: ReactivateUser) {
    return this.service.reactivateUser(id).pipe(
      tap(() => {
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Users
          : UserUsersAndPaxRoutes.Users;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(ActivateUserAsProductOwner, { cancelUncompleted: true })
  @OnSuccess({ message: 'userActivated' })
  activateUserAsProductOwner(
    ctx: StateContext<UserStateModel>,
    { id }: ActivateUserAsProductOwner
  ) {
    return this.service.setActivationStatus(id, true).pipe(
      tap(() => {
        this.updateUserActivationStatus(ctx, id, true);
      })
    );
  }

  @Action(DeactivateUserAsProductOwner, { cancelUncompleted: true })
  @OnSuccess({ message: 'userDeactivated' })
  deactivateUserAsProductOwner(
    ctx: StateContext<UserStateModel>,
    { id }: DeactivateUserAsProductOwner
  ) {
    return this.service.setActivationStatus(id, false).pipe(
      tap(() => {
        this.updateUserActivationStatus(ctx, id, false);
      })
    );
  }

  @Action(SendResetPasswordEmail, { cancelUncompleted: true })
  @OnSuccess({ message: 'resetPasswordEmailSent' })
  sendResetPasswordEmail(ctx: StateContext<UserStateModel>, { id }: ReactivateUser) {
    return this.service.sendResetPasswordEmail(id).pipe(
      tap(() => {
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Users
          : UserUsersAndPaxRoutes.Users;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(ResendConfirmationEmail, { cancelUncompleted: true })
  @OnSuccess({ message: 'confirmationEmailSent' })
  resendConfirmationEmail(ctx: StateContext<UserStateModel>, { id }: ReactivateUser) {
    return this.service.resendConfirmationEmail(id).pipe(
      tap(() => {
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Users
          : UserUsersAndPaxRoutes.Users;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(SearchUsers, { cancelUncompleted: true })
  searchUsersInDepartment(
    ctx: StateContext<UserStateModel>,
    { searchTerm, excludePausedAndDeleted }: SearchUsers
  ) {
    return this.service.searchUsers(searchTerm, excludePausedAndDeleted).pipe(
      tap((users: IUserSearchResult[]) => {
        ctx.setState(patch({ searchUsersResults: users }));
      })
    );
  }

  @Action(SearchUsersInTenant, { cancelUncompleted: true })
  searchUsersInTenant(ctx: StateContext<UserStateModel>, { searchTerm }: SearchUsersInTenant) {
    return this.service.searchUsersInTenant(searchTerm).pipe(
      tap((response: ApiResponse<IUserListResult[]>) => {
        const mappedUsers: IUserSearchResult[] = response.data.map((user) => ({
          id: user.id,
          fullName: fullName(user),
          email: user.loginEmail,
        }));
        ctx.setState(patch({ searchUsersResults: mappedUsers }));
      })
    );
  }

  @Action(GetMySignature, { cancelUncompleted: true })
  getMySignature(ctx: StateContext<UserStateModel>) {
    return this.service.getMySignature().pipe(
      tap((signature: IAttachment) => {
        ctx.setState(patch({ mySignature: signature }));
      })
    );
  }
  @Action(ClearCurrentUser, { cancelUncompleted: true })
  clearCurrentUser(ctx: StateContext<UserStateModel>) {
    ctx.setState(patch({ currentUser: undefined }));
  }

  @Action(LoadPlannedActivitySignOffPeriodsDocumentationCompleted, { cancelUncompleted: true })
  loadPlannedActivitySignOffPeriodsDocumentationCompleted(
    ctx: StateContext<UserStateModel>,
    { userId }: LoadPlannedActivitySignOffPeriodsDocumentationCompleted
  ) {
    return this.service.loadPlannedActivitySignOffPeriodsDocumentationCompleted(userId).pipe(
      tap((result: boolean) => {
        ctx.setState(patch({ plannedActivitySignOffPeriodsDocumentationCompleted: result }));
      })
    );
  }

  @Action(LoadModuleAdminNames, { cancelUncompleted: true })
  loadModuleAdminNames(ctx: StateContext<UserStateModel>) {
    return this.service.getModuleAdminNames().pipe(
      tap((moduleAdminNames: string[]) => {
        ctx.setState(patch({ moduleAdminNames }));
      })
    );
  }

  private updateUserActivationStatus(
    ctx: StateContext<UserStateModel>,
    id: string,
    isActive: boolean
  ) {
    const user = ctx
      .getState()
      .allUsersAllTenants.find((u) => u.id === id) as IUserProductOwnerView;
    ctx.setState(
      patch({
        allUsersAllTenants: updateItem<IUserProductOwnerView>(
          (u: IUserProductOwnerView | undefined) => u?.id === id,
          { ...user, isActive }
        ),
      })
    );
  }

  @Selector()
  static getUsersTotalPages(state: UserStateModel): number {
    return state.usersTotalPages;
  }

  @Selector()
  static getAllUsersAllTenantsTotalPages(state: UserStateModel): number {
    return state.allUsersAllTenantsTotalPages;
  }

  @Selector()
  static getUserOptions(state: UserStateModel): IUserOptions | undefined {
    return state.userOptions;
  }

  @Selector()
  static getAllUsers(state: UserStateModel): IUserTable[] {
    return state.users;
  }

  @Selector()
  static getAllUsersFromAllTenants(state: UserStateModel): IUserProductOwnerView[] {
    return state.allUsersAllTenants;
  }

  @Selector()
  static userOptionsLoaded(state: UserStateModel): boolean {
    return state.userOptionsLoaded;
  }

  @Selector()
  static getUserById(state: UserStateModel) {
    return state.currentUser;
  }

  @Selector()
  static showDeletedUsers(state: UserStateModel) {
    return state.showDeletedUsers;
  }

  @Selector()
  static showPausedUsers(state: UserStateModel) {
    return state.showPausedUsers;
  }

  @Selector()
  static showDeactivatedUsers(state: UserStateModel) {
    return state.showDeactivatedUsers;
  }

  @Selector()
  static searchUsersResults(state: UserStateModel): IUserSearchResult[] {
    return state.searchUsersResults;
  }

  @Selector()
  static getMySignature(state: UserStateModel) {
    return state.mySignature;
  }

  @Selector()
  static getPlannedActivitySignOffPeriodsDocumentationCompleted(
    state: UserStateModel
  ): boolean | undefined {
    return state.plannedActivitySignOffPeriodsDocumentationCompleted;
  }

  @Selector()
  static getModuleAdminNames(state: UserStateModel): string[] {
    return state.moduleAdminNames;
  }
}
function getBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

async function dispatchProfilePicture(ctx: any, user: any) {
  if (user.profilePicture) {
    try {
      const base64String = await getBase64(user.profilePicture);
      ctx.dispatch(new PatchProfilePicture(base64String));
    } catch (error) {
      console.error('Error converting file to base64:', error);
    }
  } else {
    ctx.dispatch(new PatchProfilePicture(''));
  }
}
